/**
 * Theme payload merger — Surface Layer (/01)
 *
 * Pure function. Given the four layers (fallback, tenant, user,
 * session), returns the MergedThemePayload that the provider injects.
 *
 * Resolution rule: later layers override earlier layers key-by-key.
 * `baseTheme` is whole-payload (Carbon <Theme> accepts a single value).
 */

import {
  SURFACE_THEME_FALLBACK,
  type MergedThemePayload,
  type SessionThemeEdit,
  type TenantThemeOverride,
  type UserThemePreference,
} from '@contracts/theme.contract';
import { validateThemePayload } from './validate-theme-payload';

export interface MergeInput {
  tenant?: TenantThemeOverride | null;
  user?: UserThemePreference | null;
  session?: SessionThemeEdit | null;
}

export function mergeThemePayload(input: MergeInput): MergedThemePayload {
  const layers: MergedThemePayload['resolvedFromLayers'] = ['fallback'];

  let baseTheme: MergedThemePayload['baseTheme'] = SURFACE_THEME_FALLBACK.baseTheme;
  const tokens = { ...SURFACE_THEME_FALLBACK.tokens };
  const surfaceTokens = { ...SURFACE_THEME_FALLBACK.surfaceTokens };

  const tenant = input.tenant;
  if (tenant) {
    layers.push('tenant');
    if (tenant.baseTheme) baseTheme = tenant.baseTheme;
    const validated = validateThemePayload({
      tokens: tenant.tokens,
      surfaceTokens: tenant.surfaceTokens,
    });
    Object.assign(tokens, validated.accepted);
    Object.assign(surfaceTokens, validated.surfaceAccepted);
  }

  const user = input.user;
  if (user) {
    // If tenant.forced is true, user preferences are ignored.
    if (!tenant?.forced) {
      layers.push('user');
      if (user.baseTheme) baseTheme = user.baseTheme;
      const validated = validateThemePayload({
        tokens: user.tokens,
        surfaceTokens: user.surfaceTokens,
      });
      Object.assign(tokens, validated.accepted);
      Object.assign(surfaceTokens, validated.surfaceAccepted);
    }
  }

  const session = input.session;
  if (session) {
    layers.push('session');
    if (session.baseTheme) baseTheme = session.baseTheme;
    const validated = validateThemePayload({
      tokens: session.tokens,
      surfaceTokens: session.surfaceTokens,
    });
    Object.assign(tokens, validated.accepted);
    Object.assign(surfaceTokens, validated.surfaceAccepted);
  }

  return {
    baseTheme,
    tokens,
    surfaceTokens,
    resolvedFromLayers: layers,
  };
}
